import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import { jwtSsoValidateToken, jwtSsoLogin } from '../../http';
import { saveUser } from '../../Redux/User-slice/User-slice';
import { getBen } from '../Landing/Otp/Action/getBen';
import { getAllBen } from '../Landing/Otp/Action/getAllBen';
import { getSubscriber } from '../Landing/Otp/Action/getSubscriber';
import { saveSbsSubscriberData } from '../../Redux/B2CSubscriber-Slice/Subscriber-sbs-slice';
import styles from './JwtSso.module.css';

const EdmeSso = () => {
    const [searchParams] = useSearchParams();
    const { webContext } = useParams<{ webContext: string }>();
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [checkingAuth, setCheckingAuth] = useState(true);
    const [errorMsg, setErrorMsg] = useState('');
    const hasFetched = useRef(false);

    useEffect(() => {
        if (hasFetched.current) return;
        hasFetched.current = true;

        const handleSso = async () => {
            const token = searchParams.get('token');
            // webContext comes from route param: /services/:webContext

            if (!token || !webContext) {
                setErrorMsg('Invalid or missing SSO parameters.');
                setCheckingAuth(false);
                return;
            }

            try {
                // API 1 — validate token
                const validation: any = await jwtSsoValidateToken(token, webContext);
                if (!validation.data?.valid) {
                    setErrorMsg('Authentication failed. Please try again from the partner portal.');
                    setCheckingAuth(false);
                    return;
                }

                const sessionId = validation.data?.sessionId;
                const ssoUser = validation.data?.user;

                // API 2 — process user (login or create)
                const response: any = await jwtSsoLogin(sessionId, webContext, ssoUser);
                const { data: user, isSubscriptionActive } = response.data;

                // Build user payload same as B2C flow (VerifyOtp.tsx)
                const userSlicePayload = {
                    userId: user._id,
                    name: user.fullname ?? user.name,
                    email: user.email,
                    mobileNo: `91-${user.mobile}`,
                    clientPlatform: user.clientPlatform,
                    userRole: user.userRole,
                    addBeneficiaryAllowed: user.addBeneficiaryAllowed,
                    numberOfBenAllowed: user.numberOfBenAllowed,
                    subscriberId: response.data.subscriberId,
                    beneficiaryId: response.data.beneficiaryId,
                    accessToken: response.data.accessToken,
                    refreshToken: response.data.refreshToken,
                };

                dispatch(saveUser(userSlicePayload));
                localStorage.setItem('userData', JSON.stringify(userSlicePayload));

                // Fetch and store beneficiary + subscriber data — same as normal login flow
                await getBen(userSlicePayload.beneficiaryId, dispatch);
                await getAllBen(userSlicePayload.subscriberId, dispatch);

                const subscriptionData = await getSubscriber(userSlicePayload.subscriberId);
                dispatch(saveSbsSubscriberData({ ...subscriptionData, webContext }));

                // Redirect based on subscription status
                if (isSubscriptionActive) {
                    navigate('/dashboard');
                } else {
                    navigate(`/services/${webContext}/product`);
                }

            } catch (error) {
                console.error('JWT SSO login failed:', error);
                setErrorMsg('Authentication failed. Please try again from the partner portal.');
                setCheckingAuth(false);
            }
        };

        handleSso();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (checkingAuth) {
        return (
            <div className={styles.wrapper}>
                <img
                    src="/Images/logo-transparent3.png"
                    alt="Logo"
                    className={styles.logo}
                    style={{ animation: 'pulse 1.5s ease-in-out infinite' }}
                />
                <p className={styles.message}>Verifying...</p>
            </div>
        );
    }

    if (errorMsg) {
        return (
            <div className={styles.wrapper}>
                <img src="/Images/logo-transparent3.png" alt="Logo" className={styles.logo} />
                <p className={styles.error}>{errorMsg}</p>
            </div>
        );
    }

    return null;
};

export default EdmeSso;
